import React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PageHeader, Card, Table, SearchInput, Pager, EmptyState, ErrorState,
  Button, phone,
} from '@parsina/framework';
import { Users } from 'lucide-react';
import { C } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { useAppNavigate } from '../../hooks/useAppNavigate.js';
import { useClinicScope } from '../../hooks/useClinicScope.jsx';
import { usePatients } from '../../hooks/usePatients.js';
import { DEFAULT_PAGE_SIZE, clampPage } from '../../lib/paging.js';
import { searchTerm, MIN_SEARCH_LENGTH, genderKey } from '../../lib/patients.js';
import PatientAvatar from '../../components/PatientAvatar.jsx';

// بیماران — the clinic's register, not a dental register. A patient is not
// "dental" or "medical" (DESIGN.md §2): this is the same list the منشی enrols
// into at پذیرش, read through the same service.
//
// The list is CLINIC-SCOPED OR EMPTY, never unscoped. patientmanagement ignores
// the doctor_id filter entirely — only clinic_id and search narrow anything —
// so a call without a clinic returns the platform's whole registry. A dentist
// with no membership gets the "join a clinic" state instead of a request.

const PatientsScreen = () => {
  const { t, formatNumber, formatDigits } = useTranslation();
  const navigate = useAppNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { loading: clinicsLoading, activeId, scopeState } = useClinicScope();

  const urlQuery = searchParams.get('q') || '';
  const urlPage = Number(searchParams.get('page')) || 1;

  // Keystrokes echo locally at once and reach the URL after a pause, so typing
  // neither stalls the field nor stacks a history entry per character.
  const [typed, setTyped] = React.useState(urlQuery);
  React.useEffect(() => setTyped(urlQuery), [urlQuery]);

  const commit = React.useCallback(
    (value) => {
      const next = new URLSearchParams(searchParams);
      if (value) next.set('q', value);
      else next.delete('q');
      next.delete('page'); // a new search starts at page one
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  React.useEffect(() => {
    const trimmed = typed.trim();
    if (trimmed === urlQuery) return undefined;
    // Clearing commits immediately: waiting 300 ms to show the full list again
    // reads as a broken clear button.
    if (!trimmed) {
      commit('');
      return undefined;
    }
    if (trimmed.length < MIN_SEARCH_LENGTH) return undefined;
    const timer = setTimeout(() => commit(trimmed), 300);
    return () => clearTimeout(timer);
  }, [typed, urlQuery, commit]);

  const { patients, total, loading, error, reload } = usePatients({
    clinicId: activeId,
    search: searchTerm(urlQuery),
    page: urlPage,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  // Membership resolves BEFORE the register can be asked for, and until it does
  // there is no honest answer to «how many patients». Without this the screen
  // rendered «هنوز بیماری ثبت نشده است» for the first frames of every visit:
  // clinicsLoading leaves `noClinic` false, and usePatients with a null clinicId
  // returns loading:false + an empty page by design — so a dentist with a full
  // register was told they had none, and then the list appeared underneath it.
  const busy = clinicsLoading || loading;

  const page = clampPage(urlPage, total, DEFAULT_PAGE_SIZE);
  const goPage = React.useCallback(
    (next) => {
      const params = new URLSearchParams(searchParams);
      if (next <= 1) params.delete('page');
      else params.set('page', String(next));
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  // The URL is what the hook actually pages on, so a hand-edited `?page=999`
  // would otherwise fetch offset 24950 — an empty table — while the pager below
  // rendered the clamped page and quietly disagreed with it. Once `total` is
  // known, put the URL back inside range and let the refetch follow.
  //
  // `busy`, not `loading`: while membership is still resolving `total` is 0, so
  // pageCount is 1 and a deep link to `?page=3` would be rewritten to page one
  // before the register had been asked for even once.
  React.useEffect(() => {
    if (busy || error) return;
    if (page !== urlPage) goPage(page);
  }, [busy, error, page, urlPage, goPage]);

  const columns = [
    {
      key: 'name',
      header: t('patients.columns.name'),
      render: (row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <PatientAvatar name={row.name} src={row.avatarUrl} size={30} bg={C.navy} />
          <div style={{ minWidth: 0 }}>
            {/* The row's own onClick lives on the kit's <tr>, which carries no
                tabindex and no role — so the register was reachable by mouse
                only and a keyboard user could not open a single patient. The
                NAME is the affordance: a real <button>, styled to look exactly
                like the text it replaces, so the table gains one tab stop per
                row and nothing else changes. */}
            <button
              type="button"
              onClick={(e) => {
                // The <tr> handler would fire too and push a second identical
                // history entry.
                e.stopPropagation();
                navigate('patient', { id: row.id });
              }}
              style={{
                display: 'block',
                maxWidth: '100%',
                background: 'none',
                border: 'none',
                padding: 0,
                margin: 0,
                font: 'inherit',
                fontWeight: 700,
                color: C.textPrimary,
                textAlign: 'start',
                cursor: 'pointer',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {row.name}
            </button>
            {(row.gender || row.age != null) && (
              <div style={{ fontSize: 11.5, color: C.textMuted }}>
                {[
                  genderKey(row.gender) ? t(genderKey(row.gender)) : row.gender || '',
                  row.age != null ? t('common.units.years', { count: row.age }) : '',
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'phone',
      header: t('patients.columns.phone'),
      width: 150,
      render: (row) =>
        row.phone ? (
          <span dir="ltr" style={{ unicodeBidi: 'isolate' }}>{formatDigits(phone.forDisplay(row.phone))}</span>
        ) : (
          t('common.labels.none')
        ),
    },
    {
      key: 'fileNumber',
      header: t('patients.columns.fileNumber'),
      width: 120,
      render: (row) => (row.fileNumber ? formatDigits(row.fileNumber) : t('common.labels.none')),
    },
    {
      key: 'nationalId',
      header: t('patients.columns.nationalId'),
      width: 140,
      render: (row) =>
        row.nationalId ? (
          <span dir="ltr" style={{ unicodeBidi: 'isolate' }}>{formatDigits(row.nationalId)}</span>
        ) : (
          t('common.labels.none')
        ),
    },
  ];

  // ── No USABLE clinic → no register. Said plainly, with the way out. ───────
  //
  // Two different facts, and they need two different sentences. `scopeState`
  // is the shared rule (lib/patients.js), the same one the BFF's patientScope
  // applies server-side:
  //
  //   'none'     no membership at all — join one with an invitation code.
  //   'pending'  joined, not yet accepted. Counting a pending membership as a
  //              clinic was the bug: `activeId` became its id, every query was
  //              scoped to a clinic the BFF refuses (CLINIC_FORBIDDEN), and the
  //              dentist got a retry button for something no retry can fix.
  const noClinic = !clinicsLoading && scopeState === 'none';
  const pendingClinic = !clinicsLoading && scopeState === 'pending';
  const blocked = noClinic || pendingClinic;

  const trimmedTyped = typed.trim();
  const tooShort = trimmedTyped.length > 0 && trimmedTyped.length < MIN_SEARCH_LENGTH;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: C.bg, overflow: 'hidden' }}>
      <PageHeader title={t('patients.title')} sub={t('patients.subtitle')}>
        {!blocked && !busy && !error && (
          <span style={{ fontSize: 12.5, color: C.textMuted }}>
            {t('patients.count', { count: total })}
          </span>
        )}
      </PageHeader>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '24px 32px 40px' }}>
        {blocked ? (
          <Card>
            <EmptyState
              icon={<Users size={48} color={C.textMuted} />}
              title={t(pendingClinic ? 'patients.pendingClinic.title' : 'patients.noClinic.title')}
              description={t(
                pendingClinic
                  ? 'patients.pendingClinic.description'
                  : 'patients.noClinic.description',
              )}
              action={
                <Button onClick={() => navigate('profile')}>{t('patients.noClinic.action')}</Button>
              }
            />
          </Card>
        ) : (
          <>
            <Card style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <SearchInput
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder={t('patients.search.placeholder')}
                aria-label={t('patients.search.placeholder')}
                clearLabel={t('common.actions.clearFilters')}
                // The kit hardcodes dir="rtl" on its <input>. This field accepts
                // «علی», "Ali", a phone number and a file number, and a Latin or
                // digit run typed into a forced-RTL box has its caret and its
                // punctuation reordered. `dir="auto"` follows the first strong
                // character the dentist actually types, which is the one rule
                // that is right for all four. `inputProps` is the escape hatch
                // the kit provides — it spreads last, so it wins.
                inputProps={{ dir: 'auto' }}
                style={{ flex: 1, minWidth: 220, maxWidth: 420 }}
              />
              {/* One typed character commits nothing (MIN_SEARCH_LENGTH), so the
                  list simply did not move and the field gave no reason why. */}
              {tooShort && (
                <span style={{ fontSize: 12, color: C.textMuted }}>{t('patients.search.hint')}</span>
              )}
              {urlQuery && (
                <Button variant="subtle" onClick={() => setTyped('')}>
                  {t('common.actions.clearFilters')}
                </Button>
              )}
            </Card>

            <div
              data-testid="patients-content"
              data-state={error ? 'error' : busy ? 'loading' : patients.length ? 'content' : 'empty'}
              aria-busy={busy || undefined}
            >
              {error ? (
                // role="alert" on the wrapper: the kit's Card drops a `role`
                // unless it is the onClick (button) form.
                <div role="alert">
                  <Card>
                    <ErrorState
                      title={t('patients.error.title')}
                      description={t('errors.load.patients')}
                      onRetry={reload}
                      retryLabel={t('common.actions.retry')}
                    />
                  </Card>
                </div>
              ) : (
                <Card style={{ padding: 0, overflow: 'hidden' }}>
                  {/* The REAL table while the first page is pending: the header,
                      its four column widths and the sort chrome are all chrome
                      that stays operable, and only the BODY becomes placeholder
                      rows at the real 52px height (DESIGN.md §"Loading &
                      skeleton" — region-level loading). So the first row is
                      already where the dentist is about to click, and `empty`
                      is withheld because "no rows yet" is not "no rows". */}
                  <Table
                    loading={busy}
                    skeletonRows={8}
                    columns={columns}
                    rows={patients}
                    rowKey={(row) => row.id}
                    onRowClick={(row) => navigate('patient', { id: row.id })}
                    empty={
                      // A filtered empty list and a genuinely empty register are
                      // different facts, and only one of them means "register
                      // someone".
                      urlQuery ? (
                        <EmptyState
                          icon={<Users size={44} color={C.textMuted} />}
                          title={t('patients.emptyFiltered.title')}
                          description={t('patients.emptyFiltered.description')}
                          action={
                            <Button variant="outline" onClick={() => setTyped('')}>
                              {t('common.actions.clearFilters')}
                            </Button>
                          }
                        />
                      ) : (
                        <EmptyState
                          icon={<Users size={44} color={C.textMuted} />}
                          title={t('patients.empty.title')}
                          description={t('patients.empty.description')}
                        />
                      )
                    }
                  />
                  <Pager
                    page={page}
                    total={total}
                    pageSize={DEFAULT_PAGE_SIZE}
                    onChange={goPage}
                  />
                </Card>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PatientsScreen;
